import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const FeaturedProjects = ({ projects, onViewDetails }) => {
  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center space-x-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="Star" size={24} className="text-primary" />
          </div>
          <h2 className="text-3xl font-brand-headline text-foreground">
            Featured Case Studies
          </h2>
        </div>
        <p className="text-text-secondary font-brand-body max-w-2xl mx-auto">
          Deep dives into complex projects showcasing technical expertise, problem-solving methodology, and measurable business impact.
        </p>
      </div>
      <div className="space-y-12">
        {projects?.map((project, index) => (
          <div
            key={project?.id}
            className={`flex flex-col ${
              index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
            } gap-8 lg:gap-12 items-center`}
          >
            {/* Project Image */}
            <div className="w-full lg:w-1/2">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" />
                <div className="relative bg-card rounded-2xl border border-border overflow-hidden brand-shadow group-hover:brand-shadow-lg transition-all duration-300">
                  <Image
                    src={project?.image}
                    alt={project?.title}
                    className="w-full h-64 lg:h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 via-transparent to-transparent" />
                  
                  {/* Project Status */}
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-brand-medium ${
                      project?.status === 'Live' ?'bg-success text-success-foreground' 
                        : project?.status === 'In Development' ?'bg-warning text-warning-foreground' :'bg-accent text-accent-foreground'
                    }`}>
                      {project?.status}
                    </span>
                  </div>

                  {/* Quick Links */}
                  <div className="absolute bottom-4 right-4 flex space-x-2">
                    {project?.liveUrl && (
                      <a
                        href={project?.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-background/90 backdrop-blur-sm rounded-lg text-text-primary hover:text-primary transition-brand"
                        aria-label="View live project"
                      >
                        <Icon name="ExternalLink" size={18} />
                      </a>
                    )}
                    {project?.githubUrl && (
                      <a
                        href={project?.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-background/90 backdrop-blur-sm rounded-lg text-text-primary hover:text-primary transition-brand"
                        aria-label="View source code"
                      >
                        <Icon name="Github" size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Project Content */}
            <div className="w-full lg:w-1/2 space-y-6">
              {/* Project Header */}
              <div>
                <div className="flex items-center space-x-3 mb-3">
                  <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-brand-medium rounded-full border border-primary/20">
                    {project?.category}
                  </span>
                  <span className="text-text-secondary font-brand-body text-sm">
                    {project?.year}
                  </span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-brand-headline text-foreground mb-3">
                  {project?.title}
                </h3>
                <p className="text-text-primary font-brand-body leading-relaxed">
                  {project?.fullDescription}
                </p>
              </div>

              {/* Technology Stack */}
              <div>
                <h4 className="text-sm font-brand-medium text-text-secondary mb-3 uppercase tracking-wide">
                  Technology Stack
                </h4>
                <div className="flex flex-wrap gap-2">
                  {project?.technologies?.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 bg-muted text-text-primary text-sm font-brand-medium rounded-md border border-border"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Key Metrics */}
              <div>
                <h4 className="text-sm font-brand-medium text-text-secondary mb-3 uppercase tracking-wide">
                  Key Results
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {project?.metrics?.map((metric, metricIndex) => (
                    <div key={metricIndex} className="text-center p-3 bg-muted rounded-lg">
                      <div className="text-lg font-brand-subheading text-primary">
                        {metric?.value}
                      </div>
                      <div className="text-xs text-text-secondary font-brand-body">
                        {metric?.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Challenge Highlight */}
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Target" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-brand-medium text-foreground mb-1">
                      Key Challenge
                    </h4>
                    <p className="text-text-secondary font-brand-body text-sm">
                      {project?.keyChallenge}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="default"
                  onClick={() => onViewDetails(project)}
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={18}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  View Full Case Study
                </Button>
                {project?.liveUrl && (
                  <Button
                    variant="outline"
                    asChild
                    iconName="ExternalLink"
                    iconPosition="left"
                    iconSize={18}
                    className="border-primary/20 text-primary hover:bg-primary/10"
                  >
                    <a href={project?.liveUrl} target="_blank" rel="noopener noreferrer">
                      Live Demo
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedProjects;